import { useState, useRef, useCallback } from 'react'
import { XMarkIcon, DocumentArrowUpIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'

interface ImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (options: ImportOptions) => Promise<void>
  tableName: string
  databaseName: string
  schemaName?: string
}

export interface ImportOptions {
  fileData: string
  fileFormat: 'csv' | 'json' | 'sql'
  hasHeader?: boolean
  delimiter?: string
  fileName: string
  fileSize: number
}

export function ImportModal({ isOpen, onClose, onImport, tableName, databaseName, schemaName }: ImportModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [fileFormat, setFileFormat] = useState<'csv' | 'json' | 'sql'>('csv')
  const [hasHeader, setHasHeader] = useState(true)
  const [delimiter, setDelimiter] = useState(',')
  const [isImporting, setIsImporting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [previewData, setPreviewData] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const detectFileFormat = (fileName: string): 'csv' | 'json' | 'sql' => {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
    switch (extension) {
      case 'csv':
      case 'tsv':
        return 'csv'
      case 'json':
        return 'json'
      case 'sql':
        return 'sql'
      default:
        return 'csv'
    }
  }

  const handleFileSelect = useCallback((selectedFile: File) => {
    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds 10MB limit. Selected file: ${formatFileSize(selectedFile.size)}`)
      return
    }

    setFile(selectedFile)
    
    // Auto-detect format
    const detectedFormat = detectFileFormat(selectedFile.name)
    setFileFormat(detectedFormat)
    
    // Auto-detect delimiter for TSV files
    if (selectedFile.name.toLowerCase().endsWith('.tsv')) {
      setDelimiter('\t')
    }
    
    // Read first part of file for preview using modern Blob.text() method
    const fileSlice = selectedFile.slice(0, 1000)
    fileSlice.text().then((content) => {
      // Show first 1000 characters as preview
      setPreviewData(content.substring(0, 1000))
      return
    }).catch((error) => {
      console.error('Error reading file preview:', error)
      setPreviewData('Error reading file preview')
    })
  }, [MAX_FILE_SIZE])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleImport = () => {
    if (!file) {
      toast.error('Please select a file to import')
      return
    }

    setIsImporting(true)
    try {
      // Read file as base64 data URL
      const reader = new FileReader()
      reader.onload = async (e) => {
        const result = e.target?.result
        const dataUrl = typeof result === 'string' ? result : ''
        
        const options: ImportOptions = {
          fileData: dataUrl,
          fileFormat,
          hasHeader: fileFormat === 'csv' ? hasHeader : undefined,
          delimiter: fileFormat === 'csv' ? delimiter : undefined,
          fileName: file.name,
          fileSize: file.size
        }
        
        await onImport(options)
        // Modal will be closed by parent component on successful import
      }
      reader.readAsDataURL(file)
    } catch (error) {
      console.error('Import failed:', error)
      toast.error('Failed to import data')
    } finally {
      setIsImporting(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setPreviewData('')
    setFileFormat('csv')
    setHasHeader(true)
    setDelimiter(',')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <DocumentArrowUpIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Import Data
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Import data into <span className="font-mono">{databaseName}.{schemaName ? `${schemaName}.` : ''}{tableName}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={isImporting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select File to Import
            </label>
            
            {!file ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Drag and drop your file here, or
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Browse Files
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Supported formats: CSV, JSON, SQL (Max 10MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.tsv,.json,.sql"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <DocumentArrowUpIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {file.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(file.size)} • {fileFormat.toUpperCase()} format
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                  >
                    Remove
                  </button>
                </div>
                
                {/* File Preview */}
                {previewData && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Preview:</p>
                    <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto max-h-32">
                      {previewData}
                      {file.size > 1000 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Format Settings */}
          {file && (
            <>
              {/* File Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  File Format
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'csv', label: 'CSV', description: 'Comma-separated values' },
                    { value: 'json', label: 'JSON', description: 'JavaScript Object Notation' },
                    { value: 'sql', label: 'SQL', description: 'SQL statements' }
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`flex flex-col p-3 border rounded-lg cursor-pointer ${
                        fileFormat === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={fileFormat === option.value}
                        onChange={(e) => {
                          const v = e.target.value
                          if (v === 'csv' || v === 'json' || v === 'sql') {
                            setFileFormat(v)
                          } else {
                            setFileFormat('csv')
                          }
                        }}
                        className="sr-only"
                      />
                      <div className="font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {option.description}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* CSV Options */}
              {fileFormat === 'csv' && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hasHeader"
                      checked={hasHeader}
                      onChange={(e) => { setHasHeader(e.target.checked) }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="hasHeader" className="text-sm text-gray-700 dark:text-gray-300">
                      First row contains column headers
                    </label>
                  </div>

                  <div>
                    <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Delimiter
                    </label>
                    <select
                      id="delimiter"
                      value={delimiter}
                      onChange={(e) => { setDelimiter(e.target.value) }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value=",">Comma (,)</option>
                      <option value="\t">Tab</option>
                      <option value=";">Semicolon (;)</option>
                      <option value="|">Pipe (|)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Import Summary */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Import Summary
                </h4>
                <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <div>File: <span className="font-medium">{file.name}</span></div>
                  <div>Size: <span className="font-medium">{formatFileSize(file.size)}</span></div>
                  <div>Format: <span className="font-medium">{fileFormat.toUpperCase()}</span></div>
                  <div>Target: <span className="font-medium font-mono">{databaseName}.{schemaName ? `${schemaName}.` : ''}{tableName}</span></div>
                  {fileFormat === 'csv' && (
                    <>
                      <div>Headers: <span className="font-medium">{hasHeader ? 'Yes' : 'No'}</span></div>
                      <div>Delimiter: <span className="font-medium">{delimiter === '\t' ? 'Tab' : delimiter}</span></div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={isImporting || !file}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center"
          >
            {isImporting && (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
            )}
            Import Data
          </button>
        </div>
      </div>
    </div>
  )
}